import { useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useSelector } from 'react-redux';

import { Routes } from '@/constants';
import { useBookmarkManagement } from '@/hooks';
import { AnalyticsService, EVENT_CONSTANTS } from '@/services';
import {
  playerSessionsSelectors,
  selectActiveChapterId,
  selectEpisodeCompliance,
  selectIsFreeTrialUser,
  useAppSelector,
} from '@/store';
import { ComplianceType, EpisodeCard, Referrer } from '@/types';
import { createRouteWithParams, getIsEpisodeEthics } from '@/utils';

export interface EpisodeCardComponentProps {
  episode: EpisodeCard;
  referrer?: Referrer;
  coverLoaded: boolean;
  setCoverLoaded: (coverLoaded: boolean) => void;
  hideToolbar: boolean;
  isBookmarked: boolean;
  isEpisodeEthics: boolean;
  onToggleBookmark: () => void;
  navigationLink: { pathname: string };
  impressionRef: (node: Element | null | undefined) => void;
  compliance: ComplianceType;
}

export const useEpisodeCard = ({
  episode,
  referrer,
}: {
  episode: EpisodeCard;
  referrer: Referrer;
}): EpisodeCardComponentProps => {
  const { episodeId } = episode;

  const compliance = useAppSelector(state =>
    selectEpisodeCompliance(state, episodeId),
  );
  const isFreeTrialUser = useSelector(selectIsFreeTrialUser);
  const playerSession = useAppSelector(state =>
    playerSessionsSelectors.selectById(state, episodeId),
  );
  const activeChapterId = useSelector(selectActiveChapterId);

  const [coverLoaded, setCoverLoaded] = useState(false);

  const location = {
    ...referrer,
    component: EVENT_CONSTANTS.COMPONENT.EPISODE_CARD,
    ...(episode?.relevance
      ? { episodeRelevanceBoost: episode.relevance.totalBoost }
      : {}),
  };

  const { ref: impressionRef } = useInView({
    threshold: 0,
    triggerOnce: true,
    onChange: inView => {
      if (inView) {
        AnalyticsService.episodeImpression({
          episodeId,
          location,
          referrer,
        });
      }
    },
  });

  const { toggleBookmark: onToggleBookmark, isBookmarked } =
    useBookmarkManagement({
      episodeId,
      location,
      referrer,
    });

  const navigationLink = {
    pathname: createRouteWithParams(Routes.EPISODE, {
      episodeId,
    }),
  };

  const isEpisodeEthics = getIsEpisodeEthics({
    episode,
    compliance,
  });

  const isEpisodeNotStarted =
    !playerSession?.status || playerSession.status === 'none';
  const isTrailerActive = activeChapterId === episode?.trailerId;
  const showPremium = episode?.isPremium && isFreeTrialUser;
  const hideToolbar = isEpisodeNotStarted && !isTrailerActive && !showPremium; // TODO depends on variant?

  return {
    episode,
    referrer,
    navigationLink,
    impressionRef,
    onToggleBookmark,
    isBookmarked,
    coverLoaded,
    setCoverLoaded,
    isEpisodeEthics,
    hideToolbar,
    compliance,
  };
};
